import type { ConversationBrief, ConversationMessage, ConversationQuestion } from '@/api/conversation/conversation'
import type { ConversationAttempt, ConversationChange, ConversationState } from './reducer'

type BriefSlot = ConversationBrief['slots'][number]

export type FactSpineEntry = {
  key: string
  kind: BriefSlot['kind']
  label: string
  required: boolean
  value: string
  rawValue: BriefSlot['value']
  provenance: BriefSlot['provenance']
  source?: BriefSlot['source']
}

// ConversationOpenSlot is one slot still open, named the way a reader can act on
// it. AnswerStem is what the composer is seeded with, and is empty for a slot
// answered by choosing from a list rather than by typing.
export type ConversationOpenSlot = { key: string; label: string; answerStem: string }

export type FactSpine = {
  refreshRequired: boolean
  whatIHave: Array<FactSpineEntry>
  openSlots: Array<ConversationOpenSlot>
}

function formatBriefValue(value: BriefSlot['value']): string {
  switch (value.type) {
    case 'text':
      return value.text
    case 'integer':
      return String(value.integer)
    case 'document-reference':
      return value.document.fileName || 'Workspace source'
    case 'document-references':
      return value.documents.map((document) => document.fileName || 'Workspace source').join(', ')
    case 'artifact-reference':
      return `Proposal ${value.artifact.proposalId}, section ${value.artifact.sectionId}`
  }
}

export function selectCurrentQuestions(state: ConversationState) {
  return [...state.questions].sort((left, right) => left.ordinal - right.ordinal || left.runId - right.runId)
}

export function selectCompactChanges(state: ConversationState): Array<ConversationChange> {
  return [...state.changes].sort((left, right) => left.eventId - right.eventId)
}

export function selectFactSpine(state: ConversationState, briefId: number): FactSpine | undefined {
  const brief = state.briefs.find((candidate) => candidate.briefId === briefId)
  if (brief === undefined) return undefined
  if (state.invalidatedBriefIds.includes(briefId)) {
    return { refreshRequired: true, whatIHave: [], openSlots: [] }
  }
  return {
    refreshRequired: false,
    whatIHave: brief.slots.map((slot) => ({
      key: slot.key,
      kind: slot.kind,
      label: slot.label,
      required: slot.required,
      value: formatBriefValue(slot.value),
      rawValue: slot.value,
      provenance: slot.provenance,
      ...(slot.source === undefined ? {} : { source: slot.source })
    })),
    openSlots: brief.missingSlots.map((slot) => ({
      key: slot.key,
      label: slot.label,
      answerStem: slot.answerStem
    }))
  }
}

export type ComputedProvenance =
  | { briefId: number; key: string; label: string; value: string }
  | { messageId: number; sourceDocumentIds: Array<number>; content: string }

export function selectComputedProvenance(state: ConversationState): Array<ComputedProvenance> {
  const slots: Array<ComputedProvenance> = state.briefs.flatMap((brief) =>
    brief.slots
      .filter((slot) => slot.provenance === 'validated-computation')
      .map((slot) => ({
        briefId: brief.briefId,
        key: slot.key,
        label: slot.label,
        value: formatBriefValue(slot.value)
      }))
  )
  const messages: Array<ComputedProvenance> = [...state.olderMessages, ...state.liveMessages].flatMap((message) =>
    message.author === 'computed-facts'
      ? // The markdown rides only when there is no table, so a structured
        // result is named by its query rather than by a string it no longer
        // carries.
        (message.computedResults ?? []).map((result) => ({
          messageId: message.messageId,
          sourceDocumentIds: result.sourceDocumentIds,
          content: result.table?.title ?? result.markdown ?? ''
        }))
      : []
  )
  return [...slots, ...messages]
}

export function selectOutputStaleness(state: ConversationState, messageId: number) {
  return state.outputs.find((output) => output.messageId === messageId)
}

export type TranscriptEntry =
  | { key: string; kind: 'message'; message: ConversationMessage }
  | { key: string; kind: 'change'; change: ConversationChange }
  | { key: string; kind: 'attempt'; attempt: ConversationAttempt }
  | { key: string; kind: 'question'; question: ConversationQuestion }
  // `cancelled` rides the same entry as `failed`. It is terminal, the reader caused it,
  // and nothing else on screen reports it: the panel drops its own Cancel and Retry
  // controls the moment it lands, and the transcript cards no lifecycle status.
  | { key: string; kind: 'stopped'; executionId: number; reason: 'failed' | 'cancelled' }
  | { key: string; kind: 'intake-stopped'; runId: number; reason: 'failed' | 'cancelled' }
  | { key: string; kind: 'exhausted'; entry: { runId: number; briefId: number; remainingSlotKeys: Array<string> } }

// The turn a projection belongs to, taken from the origin message of the task that
// produced it. A snapshot task carries originMessageId beside its execution, brief and
// run ids, so correlation needs no new wire field.
type TranscriptAnchor = { messageId: number; createdAtTsMs: number }

type TranscriptAnchors = {
  byExecutionId: Map<number, TranscriptAnchor>
  byBriefId: Map<number, TranscriptAnchor>
  byRunId: Map<number, TranscriptAnchor>
  // Origin messages taken straight off the message list rather than through a task. A
  // canvas refine has neither a task nor an execution, so nothing in state.tasks can place
  // its change; the origin message id it carries is itself a message id and needs no lookup.
  byMessageId: Map<number, TranscriptAnchor>
}

// Lane orders the kinds that share one anchor, in the order the six lists render in
// today, so folding them into one timeline does not also reshuffle a turn a reader
// has already read.
const transcriptLane = {
  message: 0,
  change: 1,
  attempt: 2,
  question: 3,
  stopped: 4,
  'intake-stopped': 4,
  exhausted: 5
} as const

// An unresolved anchor sorts after every message rather than before the first one: a
// projection whose task is missing from the snapshot is newer activity, not older.
const unanchored = Number.MAX_SAFE_INTEGER

type PlacedTranscriptEntry = {
  entry: TranscriptEntry
  anchorTsMs: number
  anchorMessageId: number
  lane: number
  rank: number
  subRank: number
  tieBreak: string
}

function anchorById(anchors: Map<number, TranscriptAnchor>, id: number | undefined): TranscriptAnchor | undefined {
  return id === undefined ? undefined : anchors.get(id)
}

function buildTranscriptAnchors(state: ConversationState, messages: Array<ConversationMessage>): TranscriptAnchors {
  // One walk of the transcript, one map. A task's origin anchor is the same
  // {messageId, createdAtTsMs} pair an execution-less change resolves directly, so the
  // task loop below reads this map rather than a second one built over the same list.
  const byMessageId = new Map<number, TranscriptAnchor>(
    messages.map((message) => [
      message.messageId,
      { messageId: message.messageId, createdAtTsMs: message.createdAtTsMs }
    ])
  )
  const anchors: TranscriptAnchors = {
    byExecutionId: new Map(),
    byBriefId: new Map(),
    byRunId: new Map(),
    byMessageId
  }
  for (const task of [...state.olderTasks, ...state.tasks]) {
    // A task whose origin message has not been paged in has nothing to sit beside, so
    // its projections fall through to the unresolved tail.
    const anchor = byMessageId.get(task.originMessageId)
    if (anchor === undefined) continue
    if (typeof task.executionId === 'number') anchors.byExecutionId.set(task.executionId, anchor)
    if (typeof task.briefId === 'number') anchors.byBriefId.set(task.briefId, anchor)
    // The task graph coalesces a missing run id to 0, so a task with no intake run
    // would otherwise claim anchor key 0 and mis-anchor anything that lands there.
    if (task.intakeRunId > 0) anchors.byRunId.set(task.intakeRunId, anchor)
    if (task.questionRunId !== undefined && task.questionRunId > 0) anchors.byRunId.set(task.questionRunId, anchor)
  }
  return anchors
}

function changeAnchor(change: ConversationChange, anchors: TranscriptAnchors): TranscriptAnchor | undefined {
  switch (change.kind) {
    case 'brief':
      return anchors.byBriefId.get(change.briefId)
    case 'artifact':
      // The execution stays authoritative wherever there is one. The origin-message
      // fallback covers two cases, and is wanted for both. An execution-less change is a
      // canvas refine, which has no task at all and so can never resolve through one. An
      // execution-backed change can also miss, when the task graph is truncated or its
      // task has not paged in, and that one lands on the turn that asked for it instead
      // of at the unanchored tail below everything the reader can see. Pinned by tests in
      // selectors.test.ts so the wider half stays a decision rather than an accident.
      return (
        anchorById(anchors.byExecutionId, change.executionId) ?? anchorById(anchors.byMessageId, change.originMessageId)
      )
    case 'question':
      return anchors.byBriefId.get(change.briefId) ?? anchors.byRunId.get(change.runId)
    case 'status':
    case 'error':
      return anchorById(anchors.byExecutionId, change.executionId) ?? anchorById(anchors.byRunId, change.runId)
  }
}

function placeTranscriptEntry(
  entry: TranscriptEntry,
  anchor: TranscriptAnchor | undefined,
  rank: number,
  subRank: number,
  tieBreak: string
): PlacedTranscriptEntry {
  return {
    entry,
    anchorTsMs: anchor?.createdAtTsMs ?? unanchored,
    anchorMessageId: anchor?.messageId ?? unanchored,
    lane: transcriptLane[entry.kind],
    rank,
    subRank,
    tieBreak
  }
}

// selectTranscriptTimeline orders messages and every projection of them as one
// sequence, so a change, an attempt or a question sits beside the turn that caused it
// instead of at the bottom of a bunched change history.
export type AnchoredTranscriptEntry = {
  entry: TranscriptEntry
  originMessageId?: number
}

export function selectAnchoredTranscriptTimeline(state: ConversationState): Array<AnchoredTranscriptEntry> {
  const messages = [...state.olderMessages, ...state.liveMessages]
  const anchors = buildTranscriptAnchors(state, messages)
  const placed: Array<PlacedTranscriptEntry> = [
    ...messages.map((message) =>
      placeTranscriptEntry(
        { key: `message:${message.messageId}`, kind: 'message', message },
        { messageId: message.messageId, createdAtTsMs: message.createdAtTsMs },
        message.messageId,
        0,
        ''
      )
    ),
    ...state.changes.map((change) =>
      placeTranscriptEntry(
        { key: `change:${change.eventId}`, kind: 'change', change },
        changeAnchor(change, anchors),
        change.eventId,
        0,
        ''
      )
    ),
    ...Object.values(state.attempts).map((attempt) =>
      placeTranscriptEntry(
        { key: `attempt:${attempt.executionId}:${attempt.stepKey}`, kind: 'attempt', attempt },
        anchors.byExecutionId.get(attempt.executionId),
        attempt.executionId,
        0,
        attempt.stepKey
      )
    ),
    ...state.questions.map((question) =>
      placeTranscriptEntry(
        { key: `question:${question.runId}:${question.slotKey}`, kind: 'question', question },
        anchors.byBriefId.get(question.briefId) ?? anchors.byRunId.get(question.runId),
        question.ordinal,
        question.runId,
        ''
      )
    ),
    ...Object.entries(state.executionStates)
      .filter(([, execution]) => execution.status === 'failed' || execution.status === 'cancelled')
      .map(([executionId, execution]) => [Number(executionId), execution] as const)
      .map(([executionId, execution]) =>
        placeTranscriptEntry(
          {
            key: `stopped:${executionId}`,
            kind: 'stopped',
            executionId,
            reason: execution.status === 'cancelled' ? 'cancelled' : 'failed'
          },
          anchors.byExecutionId.get(executionId),
          executionId,
          0,
          ''
        )
      ),
    ...[...new Map([...state.olderTasks, ...state.tasks].map((task) => [task.intakeRunId, task])).values()]
      .filter((task) => task.intakeStatus === 'failed' || task.intakeStatus === 'cancelled')
      .map((task) =>
        placeTranscriptEntry(
          {
            key: `intake-stopped:${task.intakeRunId}`,
            kind: 'intake-stopped',
            runId: task.intakeRunId,
            reason: task.intakeStatus === 'cancelled' ? 'cancelled' : 'failed'
          },
          anchors.byRunId.get(task.intakeRunId),
          task.intakeRunId,
          0,
          ''
        )
      ),
    ...state.exhausted.map((entry) =>
      placeTranscriptEntry(
        { key: `exhausted:${entry.runId}`, kind: 'exhausted', entry },
        anchors.byBriefId.get(entry.briefId),
        entry.runId,
        0,
        ''
      )
    )
  ]
  return placed
    .sort(
      (left, right) =>
        left.anchorTsMs - right.anchorTsMs ||
        left.anchorMessageId - right.anchorMessageId ||
        left.lane - right.lane ||
        left.rank - right.rank ||
        left.subRank - right.subRank ||
        left.tieBreak.localeCompare(right.tieBreak)
    )
    .map((placement) => ({
      entry: placement.entry,
      ...(placement.anchorMessageId === unanchored ? {} : { originMessageId: placement.anchorMessageId })
    }))
}

export function selectTranscriptTimeline(state: ConversationState): Array<TranscriptEntry> {
  return selectAnchoredTranscriptTimeline(state).map((placement) => placement.entry)
}

/**
 * The slide-version turn and the artifact-change event are one piece of news arriving as
 * two records. The turn says which slide now reads how; the change says what happened to it
 * and how many words moved. Rendered as siblings they read as a stutter, and rendered as
 * the turn alone (which is what a reader sees when the change has not arrived) the line
 * names a slide without saying anything happened to it.
 *
 * They join on the exact slide version both name, which is an identity rather than a
 * heuristic: the producer writes the version, the message linking it, and the change
 * naming it, all in one transaction. A change with no turn on screen stays unpaired and
 * keeps its own line, because suppressing it would delete the only report of the write.
 */
export type SlideVersionFacts = {
  changeByMessageId: Map<number, ConversationChange>
  pairedEventIds: Set<number>
}

export function selectSlideVersionFacts(state: ConversationState): SlideVersionFacts {
  const changeByMessageId = new Map<number, ConversationChange>()
  const pairedEventIds = new Set<number>()
  const slideChanges = state.changes.filter(
    (change) =>
      change.kind === 'artifact' &&
      change.artifactType === 'presentation-slide' &&
      change.slideId !== undefined &&
      change.versionId !== undefined
  )
  if (slideChanges.length === 0) return { changeByMessageId, pairedEventIds }
  const messageByVersion = new Map<string, number>()
  for (const message of [...state.olderMessages, ...state.liveMessages]) {
    const slideId = message.presentationSlideId
    const versionId = message.presentationSlideVersionId
    if (typeof slideId !== 'number' || typeof versionId !== 'number') continue
    messageByVersion.set(`${slideId}:${versionId}`, message.messageId)
  }
  for (const change of slideChanges) {
    if (change.kind !== 'artifact') continue
    const messageId = messageByVersion.get(`${change.slideId}:${change.versionId}`)
    if (messageId === undefined) continue
    changeByMessageId.set(messageId, change)
    pairedEventIds.add(change.eventId)
  }
  return { changeByMessageId, pairedEventIds }
}
